import { RefObject, useCallback, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { trackInternalEvent } from '@rio/tracking'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { selectors as userSelectors } from 'builder/modules/user'
import { useI18n } from 'builder/hooks/useI18n'
import { useEscapeHandler } from 'builder/hooks/useEscapeHandler'
import { actions, SectionNames, selectors } from 'builder/modules/resumeEditor'
import { useRemainingAiProfileAttempts } from 'builder/hooks/useRemainingAiProfileAttempts'
import { useClickOutsideExcept } from 'builder/hooks/useClickOutsideExcept'
import { useTourConfig } from 'builder/components/DocumentEditor/DocumentTour'
import { Props } from '../types'
import { DIALOG_ANALYTICS_LABEL } from '../constants'
import { useContentAnimation } from '../../../hooks/useContentAnimation'

const useAIProfileSummaryResult = (
  container: RefObject<HTMLElement>,
  suggestionRef: RefObject<HTMLElement>,
  props: Props,
) => {
  const { phrasesOpenerRef, editorContainerRef } = props
  const isPremiumUser = useTypedSelector(userSelectors.premiumAccess)
  const { summary: generatedContent } = useTypedSelector(selectors.aiProfile)
  const { i18n } = useI18n()
  const dispatch = useDispatch()
  const { onChangePhrasesSuggestions } = useTourConfig()
  const isAIProfileSummaryResultOpen = useTypedSelector(selectors.isAIProfileSummaryResultOpen)
  const remainingAIGenerationAttempts = useRemainingAiProfileAttempts()
  const { isAnimationActive, animateContent } = useContentAnimation(suggestionRef, generatedContent)

  const attemptLeftText = useMemo(() => {
    if (remainingAIGenerationAttempts !== Infinity) {
      return i18n.t('builder.ai_profile_summary.rephrasing_modal.attempts_left', {
        attempts_left: remainingAIGenerationAttempts,
      })
    }
  }, [i18n, remainingAIGenerationAttempts])

  useClickOutsideExcept(container, [phrasesOpenerRef, editorContainerRef], () => {
    if (isAIProfileSummaryResultOpen) {
      dispatch(actions.setIsAIProfileSummaryResultOpen({ status: false }))
      dispatch(actions.setAIProfileResultContentAnimation(false))
    }
  })

  useEscapeHandler(() => dispatch(actions.setIsAIProfileSummaryResultOpen({ status: false })))

  const handleClose = useCallback(() => {
    dispatch(actions.resetAIProfileSummaryFlow())
    dispatch(actions.setIsAIProfileSummaryResultOpen({ status: false }))
    trackInternalEvent('click_cross', {
      label: DIALOG_ANALYTICS_LABEL,
      section: SectionNames.profile,
    })
    onChangePhrasesSuggestions()
  }, [dispatch, onChangePhrasesSuggestions])

  const handleRetry = useCallback(() => {
    if (remainingAIGenerationAttempts > 0) {
      dispatch(actions.setIsAIProfileSummaryResultOpen({ status: false }))
      dispatch(actions.setAIProfileResultContentAnimation(true))
      dispatch(actions.resetGenerateAiProfileSummaryStatus())
      dispatch(actions.generateResumeAiProfileSummary())
    } else {
      dispatch(actions.setAiGenerationPaywallType('profile'))
    }
    trackInternalEvent('click_retry', {
      label: DIALOG_ANALYTICS_LABEL,
      section: SectionNames.profile,
    })
  }, [dispatch, remainingAIGenerationAttempts])

  const handleUse = useCallback(() => {
    if (generatedContent) {
      dispatch(actions.updateSimpleField({ name: 'profile', value: generatedContent }))
      dispatch(actions.resetAIProfileSummaryFlow())
      dispatch(actions.setIsAIProfileSummaryResultOpen({ status: false }))
      trackInternalEvent('click_use', {
        label: DIALOG_ANALYTICS_LABEL,
        section: SectionNames.profile,
      })
      onChangePhrasesSuggestions()
    }
  }, [dispatch, generatedContent, onChangePhrasesSuggestions])

  return {
    generatedContent,
    attemptLeftText,
    isPremiumUser,
    isAnimationActive,
    handleRetry,
    handleClose,
    animateContent,
    handleUse,
    remainingAIGenerationAttempts,
  }
}

export default useAIProfileSummaryResult
